import { useState, useRef } from "react";
import { campaigns as api, type Campaign, type SpamScoreResult } from "@/lib/api";
import { Loader2, ShieldCheck, ShieldAlert, Inbox, Smartphone, FileText, X, AlertTriangle, CheckCircle, Info } from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Strip HTML to plain text for the text-mode tab */
function htmlToText(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<li>/gi, "  • ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Score colour and label */
function scoreStyle(score: number) {
  if (score <= 3) return { color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "Low Risk" };
  if (score <= 6) return { color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Some Risk" };
  return { color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "High Risk" };
}

/** Gmail tab badge colours */
const TAB_STYLE: Record<string, string> = {
  primary:    "bg-blue-50 text-blue-700 border-blue-200",
  promotions: "bg-amber-50 text-amber-700 border-amber-200",
  spam:       "bg-red-50 text-red-700 border-red-200",
};

const TAB_LABEL: Record<string, string> = {
  primary:    "Primary",
  promotions: "Promotions",
  spam:       "Spam Folder",
};

/** Severity icon */
function SeverityIcon({ severity }: { severity: string }) {
  if (severity === "high") return <AlertTriangle size={13} className="text-red-500 shrink-0 mt-0.5" />;
  if (severity === "med")  return <Info size={13} className="text-amber-500 shrink-0 mt-0.5" />;
  return <Info size={13} className="text-slate shrink-0 mt-0.5" />;
}

// ── Gmail chrome wrapper ──────────────────────────────────────────────────────

function GmailChrome({ children, width = "100%" }: { children: React.ReactNode; width?: string }) {
  return (
    <div className="rounded-xl border border-black/[0.08] overflow-hidden" style={{ width }}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white border-b border-black/[0.06]">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-xs text-slate font-medium">Gmail</span>
      </div>
      {children}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface InboxPreviewPanelProps {
  campaign: Campaign;
  onClose: () => void;
}

// ── Main component ────────────────────────────────────────────────────────────

type PreviewTab = "desktop" | "mobile" | "text" | "score";

export default function InboxPreviewPanel({ campaign, onClose }: InboxPreviewPanelProps) {
  const [tab, setTab] = useState<PreviewTab>("desktop");
  const [scoreResult, setScoreResult] = useState<SpamScoreResult | null>(null);
  const [scoring, setScoring] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const scoringInFlight = useRef(false);

  const html = campaign.html_content || "<p>No content yet.</p>";
  const plainText = htmlToText(html);

  const handleScore = async () => {
    if (scoringInFlight.current) return;
    scoringInFlight.current = true;
    setScoring(true);
    setScoreError(null);
    try {
      const result = await api.previewScore(campaign.id);
      setScoreResult(result);
    } catch (e: any) {
      setScoreError(e.message || "Failed to score — please try again");
    } finally {
      setScoring(false);
      scoringInFlight.current = false;
    }
  };

  // Auto-run score when switching to score tab if not yet scored
  const handleTabChange = (t: PreviewTab) => {
    setTab(t);
    if (t === "score" && !scoreResult && !scoring) {
      handleScore();
    }
  };

  const ss = scoreResult ? scoreStyle(scoreResult.score) : null;

  return (
    // Slide-over panel — fixed right drawer
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-xl bg-paper flex flex-col h-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] bg-white">
          <div>
            <h2 className="font-bold text-ink">Inbox Preview</h2>
            <p className="text-xs text-slate mt-0.5 truncate max-w-[300px]">
              {campaign.name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/[0.04] text-slate transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Subject / Preheader summary */}
        <div className="px-6 py-3 bg-white border-b border-black/[0.06] space-y-1">
          <div className="flex gap-2 items-baseline">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate w-16 shrink-0">Subject</span>
            <span className="text-sm font-semibold text-ink truncate">{campaign.subject || "(no subject)"}</span>
          </div>
          <div className="flex gap-2 items-baseline">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate w-16 shrink-0">Preview</span>
            <span className="text-xs text-slate truncate">{campaign.preview_text || "(no preheader)"}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-black/[0.06] bg-white px-4">
          {([
            { id: "desktop", icon: Inbox,       label: "Desktop" },
            { id: "mobile",  icon: Smartphone,  label: "Mobile" },
            { id: "text",    icon: FileText,     label: "Plain Text" },
            { id: "score",   icon: ShieldCheck,  label: "Score" },
          ] as const).map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                tab === id
                  ? "border-coral text-coral"
                  : "border-transparent text-slate hover:text-ink"
              }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-auto p-5">

          {/* Desktop tab */}
          {tab === "desktop" && (
            <GmailChrome>
              <iframe
                srcDoc={html}
                className="w-full border-none bg-white"
                style={{ height: "520px" }}
                sandbox="allow-same-origin"
                title="Desktop email preview"
              />
            </GmailChrome>
          )}

          {/* Mobile tab */}
          {tab === "mobile" && (
            <div className="flex justify-center">
              {/* Phone frame */}
              <div className="relative" style={{ width: 375 }}>
                <div className="absolute inset-0 rounded-[2rem] border-4 border-black/20 pointer-events-none z-10" />
                <GmailChrome width="375px">
                  <iframe
                    srcDoc={html}
                    className="w-full border-none bg-white"
                    style={{ height: "520px" }}
                    sandbox="allow-same-origin"
                    title="Mobile email preview"
                  />
                </GmailChrome>
              </div>
            </div>
          )}

          {/* Plain text tab */}
          {tab === "text" && (
            <div className="rounded-xl border border-black/[0.08] bg-white p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-3">
                Plain-text fallback — shown when images are disabled
              </p>
              <pre className="text-sm text-ink font-mono whitespace-pre-wrap leading-relaxed">
                {plainText || "(no text content)"}
              </pre>
            </div>
          )}

          {/* Score tab */}
          {tab === "score" && (
            <div className="space-y-4">
              {scoring && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate">
                  <Loader2 size={28} className="animate-spin" />
                  <p className="text-sm font-medium">Analysing for spam signals…</p>
                </div>
              )}

              {scoreError && !scoring && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-3">
                  <AlertTriangle size={16} className="shrink-0" />
                  <div>
                    <p>{scoreError}</p>
                    <button onClick={handleScore} className="text-red-700 underline text-xs mt-1">Try again</button>
                  </div>
                </div>
              )}

              {scoreResult && !scoring && ss && (
                <>
                  {/* Score + tab row */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Spam score */}
                    <div className={`rounded-xl border p-4 ${ss.bg} ${ss.border}`}>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1">Spam Score</p>
                      <div className="flex items-end gap-2">
                        <span className={`text-4xl font-black ${ss.color}`}>{scoreResult.score}</span>
                        <span className="text-slate text-sm mb-1">/10</span>
                      </div>
                      <span className={`text-xs font-bold ${ss.color}`}>{ss.label}</span>
                    </div>

                    {/* Gmail tab prediction */}
                    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-2">Predicted Tab</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${TAB_STYLE[scoreResult.tab]}`}>
                        {TAB_LABEL[scoreResult.tab]}
                      </span>
                      <p className="text-xs text-slate mt-2">
                        {scoreResult.linkCount} link{scoreResult.linkCount !== 1 ? "s" : ""} detected
                      </p>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="rounded-xl border border-black/[0.06] bg-white p-4">
                    <div className="flex justify-between text-xs text-slate mb-2">
                      <span className="font-semibold text-green-600">Clean</span>
                      <span className="font-semibold text-red-600">Spam</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gradient-to-r from-green-200 via-amber-200 to-red-300 relative overflow-hidden">
                      <div
                        className="absolute top-0 bottom-0 w-3 h-3 rounded-full bg-ink -translate-x-1/2 -translate-y-0"
                        style={{ left: `${((scoreResult.score - 1) / 9) * 100}%`, top: "-1px" }}
                      />
                    </div>
                  </div>

                  {/* Issues */}
                  {scoreResult.issues.length > 0 ? (
                    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-3">Issues Found</p>
                      <div className="space-y-2">
                        {scoreResult.issues.map((issue, i) => (
                          <div key={i} className="flex gap-2 text-sm">
                            <SeverityIcon severity={issue.severity} />
                            <span className="text-ink">{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-2 text-green-700">
                      <CheckCircle size={16} className="shrink-0" />
                      <span className="text-sm font-medium">No issues found — looks good!</span>
                    </div>
                  )}

                  {/* Suggestions */}
                  {scoreResult.suggestions.length > 0 && (
                    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-3">Suggestions</p>
                      <ul className="space-y-1.5">
                        {scoreResult.suggestions.map((s, i) => (
                          <li key={i} className="flex gap-2 text-sm text-ink">
                            <span className="text-coral font-bold shrink-0">→</span>
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Rescan */}
                  <button onClick={handleScore}
                    className="w-full py-2.5 rounded-xl border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 transition-colors">
                    Re-scan
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
